/**
 * Shared shape for the OLV wallet AUTO TOP-UP settings, stored at
 * Firestore `users/{uid}/wallet/autoTopUp`.
 *
 * Opt-in, off by default. Kept intentionally minimal and self-contained so the
 * settings UI and the `useAutoTopUp` hook share one source of truth for the
 * shape and defaults. Mirrors the backend `AutoTopUpSettings`
 * (functions/src/autoTopUp.ts).
 */

export interface AutoTopUpSettings {
  /** Master switch. Off by default. */
  enabled: boolean;
  /** Trigger a top-up when the wallet USD balance drops below this. */
  thresholdUsd: number;
  /** USD amount to charge on each auto top-up. */
  topUpAmountUsd: number;
  /** Hard cap on successful auto-charges within any rolling 24h window. */
  maxChargesPer24h: number;
  /** Optional explicit saved card; when absent the customer's default PM is used. */
  paymentMethodId?: string;
  /**
   * The amount the USER last saw and approved. Suggested amounts follow the
   * account's own usage, so the server may lower a future charge freely but
   * must never raise it past this figure without asking — otherwise a
   * recomputation rebuilds the 2026-09-10 surprise somewhere new.
   */
  confirmedAmountUsd?: number;
  /** Server timestamp of the last write (Firestore Timestamp at read time). */
  updatedAt?: unknown;
}

/**
 * FALLBACK defaults, used only when the account has too little usage history
 * to suggest anything personal (functions/src/billing/burnRate.ts).
 *
 * 2026-08-14 owner directive set a flat $25 / $500. After INCIDENT 2026-09-10
 * Miles replaced that with usage-derived amounts (2026-09-11): the suggested
 * ladder centres on what the organization actually spends over a few days, a
 * week, two weeks and a month, and auto top-up targets about 5-7 days. A flat
 * $500 against a $25 trigger was a 20x ratio nobody chose.
 *
 * `enabled: false` is the STORED default and the CHECKBOX default — the box is
 * no longer pre-ticked for users who have never chosen. Keep it false so
 * reading an absent doc never charges.
 */
export const AUTO_TOP_UP_DEFAULTS: Pick<
  AutoTopUpSettings,
  'enabled' | 'thresholdUsd' | 'topUpAmountUsd' | 'maxChargesPer24h'
> = {
  enabled: false,
  thresholdUsd: 25,
  topUpAmountUsd: 500,
  maxChargesPer24h: 2,
};

/** The user-adjustable fields (everything except the `enabled` switch). */
export type AutoTopUpUpdate = Partial<
  Pick<
    AutoTopUpSettings,
    | 'thresholdUsd'
    | 'topUpAmountUsd'
    | 'maxChargesPer24h'
    | 'paymentMethodId'
    | 'confirmedAmountUsd'
  >
>;
